import isEqual from 'lodash/isEqual';

export type IObserverInstance = {
  options: IntersectionObserverInit;
  observer: IntersectionObserver;
  instances: Map<IIntersectionObserverInstance, { elements: Set<Element>; handler: IntersectionObserverCallback }>;
};

export type IIntersectionObserverService = {
  __observerInstances: IObserverInstance[];
  getInstance: (
    handler: IntersectionObserverCallback,
    options: IntersectionObserverInit,
  ) => IIntersectionObserverInstance;
};

export type IIntersectionObserverInstance = {
  observe: (el: Element) => void;
  unobserve: (el: Element) => void;
  disconnect: () => void;
};

// added this service to reduce the number of creation IntersectionObserver instances
// when options: IntersectionObserverInit is the same no creation will happen
// https://www.bennadel.com/blog/3954-intersectionobserver-api-performance-many-vs-shared-in-angular-11-0-5.htm
// it can be useful to utilizing this service on low-lewel components without requirement to collect refs in array
// or it may help with performance
export const intersectionObserverService: IIntersectionObserverService = {
  __observerInstances: [],

  getInstance(onIntersect, options) {
    let observerInstance: IObserverInstance | undefined = intersectionObserverService.__observerInstances.find(
      ({ options: instanceOptions }) => isEqual(instanceOptions, options),
    );

    if (!observerInstance) {
      const handleIntersect = (entries: IntersectionObserverEntry[], obs) => {
        const instancesOfObserver = intersectionObserverService.__observerInstances.find(
          ({ observer }) => observer === obs,
        );

        if (!instancesOfObserver) return;

        for (const [_, { elements, handler }] of instancesOfObserver.instances) {
          const entriesRelatedToInstance = entries.filter(entry => elements.has(entry.target));

          if (entriesRelatedToInstance.length > 0) {
            handler(entriesRelatedToInstance, obs);
          }
        }
      };

      const observer = new IntersectionObserver(handleIntersect, options);
      observerInstance = { options, observer, instances: new Map() };

      intersectionObserverService.__observerInstances.push(observerInstance);
    }

    const newInstance: IIntersectionObserverInstance = {
      observe(el) {
        if (!el) return;

        const { elements } = observerInstance?.instances.get(this) || {};

        if (!elements) return;

        elements.add(el);

        observerInstance?.observer?.observe(el);
      },
      unobserve(el) {
        if (!el) return;

        const { elements } = observerInstance?.instances.get(this) || {};

        if (!elements) return;

        elements.delete(el);

        observerInstance?.observer?.unobserve(el);
      },
      disconnect() {
        observerInstance?.instances.get(this)?.elements.forEach(el => {
          observerInstance?.observer.unobserve(el);
        });

        observerInstance?.instances.get(this)?.elements.clear();
        observerInstance?.instances.delete(this);

        if (observerInstance?.instances.size === 0) {
          observerInstance?.observer?.disconnect();

          intersectionObserverService.__observerInstances = intersectionObserverService.__observerInstances.filter(
            ({ observer }) => observer !== observerInstance?.observer,
          );

          observerInstance = undefined;
        }
      },
    };

    observerInstance.instances.set(newInstance, { elements: new Set(), handler: onIntersect });

    return newInstance;
  },
};
